import React, { useEffect, useState, useCallback, useMemo } from 'react';
import WorkingTypeSelect, { PaidChip, fetchWorkingTypes } from '../common/WorkingTypeSelect';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Checkbox, FormGroup, FormControlLabel, Tooltip, Switch,
  Stack, CircularProgress, Alert, Paper, alpha, Avatar, Badge,
  InputAdornment, Divider, TableContainer, Table, TableHead,
  TableBody, TableRow, TableCell, Collapse,
  FormControl, InputLabel, Select, MenuItem, Menu, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AccessTime as AccessTimeIcon, Person as PersonIcon,
  PersonAdd as PersonAddIcon, PersonRemove as PersonRemoveIcon,
  Schedule as ScheduleIcon, Search as SearchIcon,
  Coffee as CoffeeIcon, Timer as TimerIcon, Star as StarIcon,
  Groups as GroupsIcon, ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderOpenIcon, ExpandLess as ExpandLessIcon,
  DoneAll as DoneAllIcon, EditOff as EditOffIcon,
  Category as CategoryIcon, CalendarMonth as CalendarMonthIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import Toast from '../actions/Toasts';
import { useTranslation } from 'react-i18next';
import usePermissions from '../../hooks/usePermissions';
import { useHistory } from 'react-router-dom';
import { getAuthToken } from '../../services/authSession';
import { activeWorkers } from '../../utils/workerStatus';

const DAYS_EN = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAYS_SQ = { Monday:'E Hënë', Tuesday:'E Martë', Wednesday:'E Mërkurë',
                  Thursday:'E Enjte', Friday:'E Premte', Saturday:'E Shtunë', Sunday:'E Diel' };
// Shkurtesa të dallueshme (të gjitha ditët shqip nisin me "E", prandaj s'mund të
// përdorim dy shkronjat e para).
const DAYS_SHORT = { Monday:'Hë', Tuesday:'Ma', Wednesday:'Më',
                     Thursday:'En', Friday:'Pr', Saturday:'Sht', Sunday:'Di' };

const EMPTY_SCHEDULE = {
  name: '',
  start_time: '08:00',
  end_time: '16:00',
  break_minutes: 30,
  late_tolerance_minutes: 5,
  work_days: ['Monday','Tuesday','Wednesday','Thursday','Friday'],
  absence_alert_enabled: false,
  absence_alert_minutes: 15,
  absence_alert_recipients: [],
  measure_interval_minutes: 0,
  // Parazgjedhja e tipit të punës për turnin (working_types); bosh → PRODUCTIVE.
  working_type_code: '',
};

function fmtTime(t) {
  if (!t) return '—';
  return String(t).slice(0, 5);
}

function calcNetMinutes(start, end, breakMin) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let totalMin = (eh * 60 + em) - (sh * 60 + sm) - (Number(breakMin) || 0);
  if (totalMin <= 0) return null;
  return totalMin;
}

function fmtHM(totalMin) {
  if (totalMin == null) return null;
  const h = Math.floor(totalMin / 60);
  const m = Math.round(totalMin % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function calcWorkHours(start, end, breakMin) {
  return fmtHM(calcNetMinutes(start, end, breakMin));
}

// Përmbledh ku janë punëtorët: numëron sipas departamentit (emrat zgjidhen nga
// lista e departamenteve, me rezervë DepartmentName nga API ose pozita).
function groupByLocation(wf, deptById) {
  const counts = new Map();
  (wf || []).forEach(w => {
    const id = w.Department ?? w.department_id;
    const name = (id != null && deptById.get(String(id)))
      || w.DepartmentName || w.BranchName || w.Position || 'Pa departament';
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

// ── Day selector ─────────────────────────────────────────────────────────────
function DaySelector({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: 'text.secondary', fontSize: '0.68rem' }}>
        {t('workSchedules.workDaysLabel')}
      </Typography>
      <Stack direction="row" spacing={0.5} mt={0.75}>
        {DAYS_EN.map((day) => {
          const active = value.includes(day);
          return (
            <Tooltip key={day} title={DAYS_SQ[day]} arrow>
              <Box onClick={() => onChange(day)} sx={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.72rem', userSelect: 'none',
                transition: 'all .15s',
                bgcolor: active ? 'primary.main' : t => alpha(t.palette.action.hover, 2),
                color: active ? '#fff' : 'text.secondary',
                border: '2px solid', borderColor: active ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' },
              }}>
                {DAYS_SHORT[day]}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────────────
/**
 * Krijimi/ndryshimi i një orari pune — PROPOZIM DIZAJNI.
 *
 * Çfarë ishte: një dialog `md` me dy kolona ku emri, orët, pauza, toleranca,
 * intervali i matjes dhe njoftimet e mungesës qëndronin të gjitha njëherësh me
 * peshë të barabartë. Nëntë kontrolle në një ekran, dhe asnjëra nuk thoshte se
 * cila është ajo që duhet plotësuar patjetër.
 *
 * Çfarë është tani, dhe pse:
 *
 *  · **Një kolonë, tri seksione.** Në një dialog `sm` gjërat lexohen nga lart
 *    poshtë. Dy kolonat e detyronin syrin të kërcente, dhe në tablet
 *    shtypeshin njëra mbi tjetrën gjithsesi.
 *
 *  · **Thelbi lart.** Emri, ora e fillimit dhe e mbarimit, ditët e punës — ato
 *    përcaktojnë çfarë ËSHTË ky orar. Pauza dhe toleranca vijnë menjëherë pas
 *    tyre sepse e ndryshojnë rezultatin e orëve.
 *
 *  · **Matja dhe njoftimet palosen.** Të dyja janë politika, jo identitet i
 *    orarit, dhe shumica e orareve nuk i prekin kurrë. Palosja hapet vetvetiu
 *    kur ato tashmë kanë vlerë të vendosur — asgjë e konfiguruar nuk fshihet.
 *
 *  · **Gabimet rrinë te fusha.** Butoni i çaktivizuar pa arsye është enigmë;
 *    tani çdo fushë e detyrueshme e thotë vetë çfarë i mungon, dhe vetëm pasi
 *    njeriu e ka prekur ose ka provuar të ruajë — një formular që skuqet sapo
 *    hapet i mëson njerëzit ta shpërfillin të kuqen.
 *
 * BIZNESI NUK NDRYSHOI. I njëjti `form`, i njëjti `onSave(form)`, e njëjta
 * rregull vlefshmërie (emër + dy orë + të paktën një ditë). Asnjë fushë nuk u
 * shtua dhe asnjë nuk u hoq.
 */
function ScheduleDialog({ open, initial, onClose, onSave, saving, workers = [] }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY_SCHEDULE });
  const [touched, setTouched] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = initial ? { ...EMPTY_SCHEDULE, ...initial } : { ...EMPTY_SCHEDULE };
    setForm(next);
    setTouched({});
    // Politika e fshehur nuk guxon të mbetet e padukshme: nëse ky orar tashmë
    // rrumbullakos orët ose njofton për mungesa, seksioni hapet vetë.
    setShowAdvanced(!!next.absence_alert_enabled || Number(next.measure_interval_minutes) > 0);
  }, [open, initial]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const touch = (k) => setTouched(p => (p[k] ? p : { ...p, [k]: true }));

  const toggleDay = (day) => {
    touch('work_days');
    setForm(p => ({
      ...p,
      work_days: p.work_days.includes(day)
        ? p.work_days.filter(d => d !== day)
        : [...p.work_days, day],
    }));
  };

  const isEdit = !!initial?.id;
  const workH  = calcWorkHours(form.start_time, form.end_time, form.break_minutes);
  // Fundi para fillimit është turn nate, jo gabim — `calcNetMinutes` thjesht nuk
  // jep orë neto për të, ndaj thuhet me fjalë në vend që të mbetet një boshllëk.
  const overnight = !!form.start_time && !!form.end_time && form.end_time <= form.start_time;

  const errors = {
    name:       form.name.trim()        ? '' : t('workSchedules.errName', 'Emri i orarit është i detyrueshëm.'),
    start_time: form.start_time         ? '' : t('workSchedules.errStart', 'Cakto orën e fillimit.'),
    end_time:   form.end_time           ? '' : t('workSchedules.errEnd', 'Cakto orën e mbarimit.'),
    work_days:  form.work_days.length   ? '' : t('workSchedules.errDays', 'Zgjidh të paktën një ditë pune.'),
  };
  const valid = !Object.values(errors).some(Boolean);
  const show  = (k) => (touched[k] || touched._submit) && !!errors[k];

  const submit = () => {
    if (!valid) { setTouched(p => ({ ...p, _submit: true })); return; }
    onSave(form);
  };

  // Sugjerime marrësish nga punëtorët (email + emër) për njoftimin e mungesës
  const recipientOptions = useMemo(
    () => (workers || []).filter(w => w.Email || w.email).map(w => (w.Email || w.email)),
    [workers]
  );
  const emailNameMap = useMemo(() => {
    const m = {};
    (workers || []).forEach(w => {
      const em = w.Email || w.email;
      if (em) m[em] = (w.FullName || `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()) || em;
    });
    return m;
  }, [workers]);

  /* Titulli i një seksioni — një rresht, jo një kartelë. Kutitë brenda kutive e
   * bënin dialogun të dukej më i rëndë se ç'është. */
  const SectionLabel = ({ children }) => (
    <Typography variant="overline" sx={{
      display: 'block', color: 'text.secondary', fontWeight: 800,
      letterSpacing: '0.09em', fontSize: '0.66rem', lineHeight: 2,
    }}>
      {children}
    </Typography>
  );

  return (
    <Dialog
      open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar sx={{ bgcolor: 'primary.soft', color: 'primary.main', width: 36, height: 36 }}>
          <ScheduleIcon sx={{ fontSize: 19 }} />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.25 }} noWrap>
            {isEdit ? t('workSchedules.editSchedule') : t('workSchedules.newSchedule')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {isEdit && form.name ? form.name : t('workSchedules.scheduleDialogSubtitle')}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1.5 }}>
        <Stack spacing={2.25}>

          {/* ── 1. Çfarë është ky orar ─────────────────────────────────── */}
          <Box>
            <TextField
              fullWidth size="small" autoFocus
              label={t('workSchedules.scheduleName')}
              value={form.name}
              onChange={e => f('name', e.target.value)}
              onBlur={() => touch('name')}
              error={show('name')}
              helperText={show('name') ? errors.name : ' '}
              placeholder={t('workSchedules.scheduleNamePlaceholder')}
              InputProps={{ sx: { borderRadius: 2 } }}
              sx={{ '& .MuiFormHelperText-root': { mt: 0.25, mb: -0.5 } }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                fullWidth size="small" type="time"
                label={t('workSchedules.startTime')}
                value={form.start_time}
                onChange={e => f('start_time', e.target.value)}
                onBlur={() => touch('start_time')}
                error={show('start_time')}
                helperText={show('start_time') ? errors.start_time : ''}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: 2 } }}
              />
              <TextField
                fullWidth size="small" type="time"
                label={t('workSchedules.endTime')}
                value={form.end_time}
                onChange={e => f('end_time', e.target.value)}
                onBlur={() => touch('end_time')}
                error={show('end_time')}
                helperText={show('end_time') ? errors.end_time : ''}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Box>

            {/* Rezultati i zgjedhjes, aty ku bëhet zgjedhja — jo diku më poshtë. */}
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
              {workH && (
                <Chip icon={<TimerIcon />} label={`${workH} ${t('workSchedules.netWork')}`}
                  color="primary" size="small" sx={{ fontWeight: 700 }} />
              )}
              {form.break_minutes > 0 && (
                <Chip icon={<CoffeeIcon />} label={`${form.break_minutes}min ${t('workSchedules.break')}`}
                  size="small" variant="outlined" />
              )}
              {overnight && (
                <Chip size="small" variant="outlined" color="warning"
                  label={t('workSchedules.overnightHint', 'Turn nate — kalon mesnatën')} />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* ── 2. Kur punohet ─────────────────────────────────────────── */}
          <Box>
            <DaySelector value={form.work_days} onChange={toggleDay} />
            {show('work_days') && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                {errors.work_days}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* ── 3. Sa hiqet dhe sa falet ───────────────────────────────── */}
          <Box>
            <SectionLabel>{t('workSchedules.sectionSchedule')}</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                fullWidth size="small" type="number"
                label={t('workSchedules.breakMinutes')}
                value={form.break_minutes}
                onChange={e => f('break_minutes', Number(e.target.value))}
                inputProps={{ min: 0, max: 120 }}
                InputProps={{ sx: { borderRadius: 2 },
                  startAdornment: <InputAdornment position="start"><CoffeeIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">min</InputAdornment> }}
              />
              <TextField
                fullWidth size="small" type="number"
                label={t('workSchedules.lateTolerance')}
                value={form.late_tolerance_minutes}
                onChange={e => f('late_tolerance_minutes', Number(e.target.value))}
                inputProps={{ min: 0, max: 60 }}
                helperText={t('workSchedules.lateToleranceHelp')}
                InputProps={{ sx: { borderRadius: 2 },
                  endAdornment: <InputAdornment position="end">min</InputAdornment> }}
              />
            </Box>
            {/* Lloji i punës i turnit: çdo caktim pa tip të vetin e trashëgon. */}
            <Box sx={{ mt: 1.5 }}>
              <WorkingTypeSelect value={form.working_type_code || ''} onChange={(code) => f('working_type_code', code || '')}
                emptyLabel="Parazgjedhja (PRODUCTIVE — punë e paguar)" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>

          {/* ── 4. Politika — e palosur derisa të nevojitet ─────────────── */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
            <Box
              onClick={() => setShowAdvanced(v => !v)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAdvanced(v => !v); } }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', py: 0.5,
                borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
            >
              {showAdvanced ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t('workSchedules.sectionTracking')}
              </Typography>
              {!showAdvanced && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 0.5 }}>
                  {form.absence_alert_enabled
                    ? t('workSchedules.alertOnShort', 'njoftimi i mungesës aktiv')
                    : t('workSchedules.advancedHint', 'rrumbullakimi i orëve, njoftimi i mungesës')}
                </Typography>
              )}
            </Box>

            <Collapse in={showAdvanced} unmountOnExit>
              <Stack spacing={1.75} sx={{ pt: 1.25 }}>
                <TextField
                  fullWidth size="small" type="number"
                  label={t('workSchedules.measureInterval')}
                  value={form.measure_interval_minutes}
                  onChange={e => f('measure_interval_minutes', e.target.value)}
                  onBlur={e => f('measure_interval_minutes', e.target.value === '' ? 0 : Math.max(0, Math.min(60, Number(e.target.value))))}
                  inputProps={{ min: 0, max: 60 }}
                  helperText={t('workSchedules.measureIntervalHelp')}
                  InputProps={{ sx: { borderRadius: 2 },
                    startAdornment: <InputAdornment position="start"><TimerIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
                    endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                />

                <Box sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  bgcolor: t => alpha(t.palette.warning.main, form.absence_alert_enabled ? 0.06 : 0) }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <NotificationsActiveIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                          {t('workSchedules.absenceAlertLabel')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                          {t('workSchedules.absenceAlertHelp')}
                        </Typography>
                      </Box>
                    </Stack>
                    <Switch
                      checked={!!form.absence_alert_enabled}
                      onChange={e => f('absence_alert_enabled', e.target.checked)}
                      color="warning"
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>

                  <Collapse in={!!form.absence_alert_enabled} unmountOnExit>
                    <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                      <TextField
                        fullWidth size="small" type="number"
                        label={t('workSchedules.absenceAlertMinutes')}
                        value={form.absence_alert_minutes}
                        onChange={e => f('absence_alert_minutes', e.target.value)}
                        onBlur={e => f('absence_alert_minutes', e.target.value === '' ? 15 : Math.max(0, Math.min(240, Number(e.target.value))))}
                        inputProps={{ min: 0, max: 240 }}
                        helperText={t('workSchedules.absenceAlertMinutesHelp')}
                        InputProps={{ sx: { borderRadius: 2 },
                          endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                      />
                      <Autocomplete
                        multiple freeSolo size="small"
                        options={recipientOptions}
                        value={form.absence_alert_recipients || []}
                        onChange={(e, val) => f('absence_alert_recipients',
                          [...new Set(val.map(s => String(s).trim()).filter(Boolean))])}
                        getOptionLabel={(o) => o}
                        filterSelectedOptions
                        renderOption={(props, o) => (
                          <li {...props} key={o}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {emailNameMap[o] || o}
                              </Typography>
                              {emailNameMap[o] && (
                                <Typography variant="caption" color="text.secondary" noWrap>{o}</Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((opt, i) => (
                            <Chip size="small" key={opt} label={emailNameMap[opt] || opt}
                              {...getTagProps({ index: i })} sx={{ height: 22 }} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField {...params}
                            label={t('workSchedules.absenceRecipients')}
                            placeholder={t('workSchedules.absenceRecipientsPlaceholder')}
                            helperText={t('workSchedules.absenceRecipientsHelp')}
                          />
                        )}
                      />
                    </Stack>
                  </Collapse>
                </Box>
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />
      {/* Veprimi kryesor rri djathtas dhe vetëm; anulimi është tekst, jo buton i
          barasvlershëm. Butoni nuk çaktivizohet për vlefshmërinë — klikimi i tij
          i tregon fushat që mungojnë, në vend që të mos ndodhë asgjë. */}
      <DialogActions sx={{ px: 2.5, py: 1.75 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving}
          sx={{ borderRadius: 2, minWidth: 104 }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {isEdit ? t('common.save') : t('workSchedules.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignDialog({ open, schedule, workers, departments, projects, initialSearch, editMode, onClose, onAssign, onRemove, loadingAssign }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [assignedIds, setAssignedIds] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const unassignedFiltered = (filtered) => filtered.filter(w => !assignedIds.includes(w.Id));

  const handleSelectAll = (filtered) => {
    const unassigned = unassignedFiltered(filtered);
    const allSelected = unassigned.every(w => selectedIds.has(w.Id));
    setSelectedIds(allSelected ? new Set() : new Set(unassigned.map(w => w.Id)));
  };

  const handleBulkAssign = async (filtered) => {
    const ids = [...selectedIds].filter(id => !assignedIds.includes(id));
    if (!ids.length) return;
    setBatchLoading(true);
    for (const id of ids) {
      try {
        await onAssign(id, effectiveFrom);
        setAssignedIds(p => [...p, id]);
      } catch { /* skip */ }
    }
    setSelectedIds(new Set());
    setBatchLoading(false);
  };

  useEffect(() => {
    if (!open || !schedule?.id) { setAssignedIds([]); setSearch(''); setFilterDept(''); setFilterProject(''); setSelectedIds(new Set()); return; }
    if (initialSearch) setSearch(initialSearch);
    setLoadingEmployees(true);
    apiService().getById('workSchedules', `${schedule.id}/employees`)
      .then(r => setAssignedIds((r?.data || []).map(e => e.worker_id)))
      .catch(() => {})
      .finally(() => setLoadingEmployees(false));
  }, [open, schedule?.id, initialSearch]);

  // Full department list as {id, name}: the org list (from the API) unioned
  // with any departments found on worker profiles — so the dropdown is complete
  // even when a department has no assigned worker yet. Keyed by id because that
  // is what worker records reference (worker.Department / .DepartmentIds).
  const deptOptions = useMemo(() => {
    const byId = new Map();
    (departments || []).forEach(d => {
      const id = d.Id ?? d.id; const name = d.Name || d.name || d.DepartmentName || d.department_name;
      if (id != null && name) byId.set(String(id), name);
    });
    (workers || []).forEach(w => {
      const id = w.Department ?? w.department_id; const name = w.DepartmentName || w.department_name;
      if (id != null && name && !byId.has(String(id))) byId.set(String(id), name);
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [departments, workers]);

  // Show every project (not only those that already have an assigned worker);
  // selecting one simply filters the worker list below.
  const projectOptions = useMemo(() => (projects || []), [projects]);

  const filtered = (workers || []).filter(w => {
    const q = search.toLowerCase();
    const nameMatch = !q || (w.FirstName + ' ' + w.LastName).toLowerCase().includes(q)
      || (w.Position || '').toLowerCase().includes(q);
    const deptIds = [w.Department, ...(w.DepartmentIds || w.department_ids || [])].filter(v => v != null).map(String);
    const deptMatch = !filterDept || deptIds.includes(filterDept);
    const projMatch = !filterProject || (
      (w.ProjectIds || w.project_ids || []).map(String).includes(filterProject)
    );
    return nameMatch && deptMatch && projMatch;
  });

  const handleAssignAll = async () => {
    const unassigned = filtered.filter(w => !assignedIds.includes(w.Id));
    if (!unassigned.length) return;
    setBatchLoading(true);
    for (const w of unassigned) {
      try {
        await onAssign(w.Id, effectiveFrom);
        setAssignedIds(p => [...p, w.Id]);
      } catch { /* skip */ }
    }
    setBatchLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {t('workSchedules.assignScheduleColon')} <span style={{ color: '#1976d2' }}>{schedule?.name}</span>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Stack spacing={1.5}>
          <TextField
            fullWidth size="small" type="date" label={t('workSchedules.effectiveFrom')}
            value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          {/* Filter by Department or Project from worker profile */}
          <Stack direction="row" spacing={1}>
            <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
              <InputLabel>{t('worker.departments')}</InputLabel>
              <Select value={filterDept} label={t('worker.departments')} onChange={e => { setFilterDept(e.target.value); setFilterProject(''); }}>
                <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                {deptOptions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
              <InputLabel>{t('worker.projects')}</InputLabel>
              <Select value={filterProject} label={t('worker.projects')} onChange={e => { setFilterProject(e.target.value); setFilterDept(''); }}>
                <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
                {projectOptions.map(p => <MenuItem key={p.Id || p.id} value={String(p.Id || p.id)}>{p.Name || p.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            fullWidth size="small" placeholder={t('workSchedules.searchWorker')}
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />

          {editMode && !loadingEmployees && (
            <Stack direction="row" alignItems="center" sx={
              { px: 1, py: 0.5, bgcolor: t => alpha(t.palette.primary.main, 0.04), borderRadius: 1.5 }
            }>
              <Checkbox
                size="small"
                checked={filtered.length > 0 && unassignedFiltered(filtered).length > 0 && unassignedFiltered(filtered).every(w => selectedIds.has(w.Id))}
                indeterminate={selectedIds.size > 0 && !unassignedFiltered(filtered).every(w => selectedIds.has(w.Id))}
                onChange={() => handleSelectAll(filtered)}
                sx={{ p: 0.5 }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {selectedIds.size > 0
                  ? t('workSchedules.selectedCount', { count: selectedIds.size, defaultValue: `${selectedIds.size} selected` })
                  : t('workSchedules.selectAll', 'Select all unassigned')}
              </Typography>
            </Stack>
          )}

          {loadingEmployees
            ? <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            : (
              <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto', borderRadius: 1.5 }}>
                {filtered.length === 0
                  ? <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>{t('workSchedules.noWorkerFound')}</Typography>
                  : filtered.map(w => {
                    const isAssigned = assignedIds.includes(w.Id);
                    return (
                      <Box key={w.Id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider',
                        '&:last-child': { borderBottom: 0 },
                        bgcolor: editMode && selectedIds.has(w.Id)
                          ? t => alpha(t.palette.primary.main, 0.08)
                          : isAssigned ? t => alpha(t.palette.primary.main, 0.04) : 'transparent',
                        cursor: editMode && !isAssigned ? 'pointer' : 'default',
                      }}
                      onClick={() => { if (editMode && !isAssigned) toggleSelect(w.Id); }}>
                        {editMode && !isAssigned
                          ? <Checkbox size="small" checked={selectedIds.has(w.Id)} onChange={() => toggleSelect(w.Id)} onClick={e => e.stopPropagation()} sx={{ p: 0.25 }} />
                          : <PersonIcon sx={{ fontSize: 18, color: isAssigned ? 'primary.main' : 'text.disabled' }} />
                        }
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {w.FirstName} {w.LastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {w.Position || t('workSchedules.noPosition')}
                          </Typography>
                        </Box>
                        {isAssigned
                          ? (
                            <Tooltip title={t('workSchedules.removeFromSchedule')}>
                              <IconButton size="small" color="error"
                                disabled={loadingAssign}
                                onClick={() => { onRemove(w.Id); setAssignedIds(p => p.filter(x => x !== w.Id)); }}>
                                <PersonRemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('workSchedules.assignThisSchedule')}>
                              <IconButton size="small" color="primary"
                                disabled={loadingAssign}
                                onClick={() => { onAssign(w.Id, effectiveFrom); setAssignedIds(p => [...p, w.Id]); }}>
                                <PersonAddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                      </Box>
                    );
                  })
                }
              </Paper>
            )
          }
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {editMode && selectedIds.size > 0 && (
          <Button
            variant="contained" size="small" color="primary"
            startIcon={batchLoading ? <CircularProgress size={14} color="inherit" /> : <DoneAllIcon />}
            disabled={batchLoading || loadingAssign}
            onClick={() => handleBulkAssign(filtered)}
            sx={{ borderRadius: 2, textTransform: 'none', mr: 'auto' }}
          >
            {t('workSchedules.assignSelected', { count: [...selectedIds].filter(id => !assignedIds.includes(id)).length, defaultValue: `Assign ${[...selectedIds].filter(id => !assignedIds.includes(id)).length}` })}
          </Button>
        )}
        {!editMode && filtered.some(w => !assignedIds.includes(w.Id)) && (
          <Button
            variant="outlined" size="small"
            disabled={batchLoading || loadingAssign}
            onClick={handleAssignAll}
            sx={{ borderRadius: 2, textTransform: 'none', mr: 'auto' }}
          >
            {batchLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
            {t('workSchedules.assignAll', { count: filtered.filter(w => !assignedIds.includes(w.Id)).length })}
          </Button>
        )}
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Project Group Panel ─────────────────────────────────────────────────────
function ProjectGroupPanel({ projects, workers, onAssignWorker }) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);

  // Build a lookup: workerId → worker object
  const workerMap = useMemo(() => {
    const m = {};
    (workers || []).forEach(w => { m[String(w.Id || w.id)] = w; });
    return m;
  }, [workers]);

  // Filter projects that have at least one known worker
  const activeProjects = useMemo(() =>
    (projects || []).filter(p => {
      const team = (p.Team || p.team || []).map(String);
      return team.some(id => workerMap[id]);
    })
  , [projects, workerMap]);

  if (!activeProjects.length) return null;

  return (
    <Box mb={3}>
      <Typography variant="caption" sx={{
        fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'text.secondary', fontSize: '0.68rem', display: 'block', mb: 1,
      }}>
        {t('workSchedules.groupByProject', 'Projects')}
      </Typography>
      <Stack spacing={1}>
        {activeProjects.map(p => {
          const pid     = String(p.Id || p.id);
          const isOpen  = selectedId === pid;
          const color   = p.Color || p.color || '#3E8FA3';
          const team    = (p.Team || p.team || []).map(String);
          const members = team.map(id => workerMap[id]).filter(Boolean);

          return (
            <Card key={pid} variant="outlined" sx={{
              borderRadius: 2,
              borderLeft: `4px solid ${color}`,
              transition: 'box-shadow .15s',
              '&:hover': { boxShadow: 2 },
            }}>
              {/* Project row */}
              <Box
                onClick={() => setSelectedId(isOpen ? null : pid)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none',
                }}>
                <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 32, height: 32 }}>
                  <FolderOpenIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {p.Name || p.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {members.length} {t('workSchedules.workers', 'workers')}
                  </Typography>
                </Box>
                <Chip
                  label={p.Status || p.status || 'Active'}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: alpha(color, 0.1), color,
                  }}
                />
                {isOpen
                  ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                }
              </Box>

              {/* Worker list */}
              <Collapse in={isOpen} unmountOnExit>
                <Divider />
                <Box sx={{ bgcolor: t => t.palette.mode === 'dark' ? alpha('#fff', 0.02) : '#f8fafc' }}>
                  {members.map((w, i) => (
                    <Box key={w.Id || w.id} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 3, py: 1,
                      borderBottom: i < members.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.7rem' }}>
                        {(w.FirstName || w.first_name || '?')[0]}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {w.FirstName || w.first_name} {w.LastName || w.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {w.Position || w.position || '—'}
                          {(w.Department || w.department) ? ` · ${w.Department || w.department}` : ''}
                        </Typography>
                      </Box>
                      <Tooltip title={t('workSchedules.assignScheduleToWorker')}>
                        <IconButton size="small" color="primary"
                          onClick={e => { e.stopPropagation(); onAssignWorker(w); }}
                          sx={{ borderRadius: 1.5, '&:hover': { bgcolor: t => alpha(t.palette.primary.main, 0.08) } }}>
                          <PersonAddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Working Types Dialog ──────────────────────────────────────────────────────
// WORKING_TYPES: Add new types to the `worktype` store in the backend (work_types table).
// Each type maps to { Id, Name } — the dialog reads them dynamically.
function WorkingTypeDialog({ open, workers, workTypes, onClose, onApply, applying }) {
  const [selectedType, setSelectedType] = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [search, setSearch]             = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState(new Set());

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setSelectedType('');
      setDateFrom(today);
      setDateTo('');
      setSearch('');
      setSelectedWorkers(new Set());
    }
  }, [open]);

  const filtered = useMemo(() =>
    (workers || []).filter(w => {
      const q = search.toLowerCase();
      return !q || (w.FirstName + ' ' + w.LastName).toLowerCase().includes(q)
        || (w.Position || '').toLowerCase().includes(q);
    }), [workers, search]);

  const allSelected  = filtered.length > 0 && filtered.every(w => selectedWorkers.has(w.Id));
  const someSelected = filtered.some(w => selectedWorkers.has(w.Id));

  const toggle    = (id) => setSelectedWorkers(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleAll = () =>
    setSelectedWorkers(allSelected ? new Set() : new Set(filtered.map(w => w.Id)));

  const canApply = selectedType && dateFrom && dateTo && dateFrom <= dateTo && selectedWorkers.size > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
          <CategoryIcon sx={{ fontSize: 18 }} />
        </Avatar>
        Working Types
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {/* 1. Working type selector */}
          <FormControl fullWidth size="small">
            <InputLabel>Lloji i punës</InputLabel>
            <Select value={selectedType} label="Lloji i punës"
              onChange={e => setSelectedType(e.target.value)}
              sx={{ borderRadius: 2 }}>
              {(workTypes || []).map(wt => (
                <MenuItem key={wt.code} value={wt.code} sx={{ gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: wt.color || '#64748B', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{wt.name}</Typography>
                  <PaidChip paid={wt.isPaid} accrue={wt.payrollImpact === 'ACCRUE'} />
                </MenuItem>
              ))}
            </Select>
            {selectedType && (() => { const wt = (workTypes || []).find(x => x.code === selectedType); return wt ? (
              <Typography variant="caption" sx={{ mt: .5, display: 'block', color: wt.payrollImpact === 'ACCRUE' ? 'warning.dark' : wt.isPaid === false ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
                {wt.name}: {wt.payrollImpact === 'ACCRUE' ? 'akumulohet te banka e orëve (ditë pushimi)' : wt.isPaid === false ? 'NUK paguhet' : 'paguhet'} · zëvendëson tipin e orarit për ditët e zgjedhura (lejet e aprovuara mbeten)
              </Typography>) : null; })()}
          </FormControl>

          {/* 2. Time interval */}
          <Stack direction="row" spacing={1.5}>
            <TextField fullWidth size="small" type="date" label="Nga data"
              value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: 2 } }} />
            <TextField fullWidth size="small" type="date" label="Deri më"
              value={dateTo} onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: 2 } }} />
          </Stack>

          {/* 3. Worker search + multi-select */}
          <TextField fullWidth size="small" placeholder="Kërko punëtor..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2 },
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            }} />

          <Stack direction="row" alignItems="center"
            sx={{ px: 1, py: 0.5, bgcolor: t => alpha(t.palette.secondary.main, 0.06), borderRadius: 1.5 }}>
            <Checkbox size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleAll} sx={{ p: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'secondary.main' }}>
              {selectedWorkers.size > 0 ? `${selectedWorkers.size} të zgjedhur` : 'Zgjidh të gjithë'}
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto', borderRadius: 1.5 }}>
            {filtered.length === 0
              ? <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>Nuk u gjet asnjë punëtor</Typography>
              : filtered.map(w => (
                <Box key={w.Id} onClick={() => toggle(w.Id)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 0.75, cursor: 'pointer',
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  bgcolor: selectedWorkers.has(w.Id) ? t => alpha(t.palette.secondary.main, 0.07) : 'transparent',
                  '&:hover': { bgcolor: t => alpha(t.palette.secondary.main, 0.04) },
                }}>
                  <Checkbox size="small" checked={selectedWorkers.has(w.Id)}
                    onChange={() => toggle(w.Id)} onClick={e => e.stopPropagation()} sx={{ p: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {w.FirstName} {w.LastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {w.Position || '—'}
                    </Typography>
                  </Box>
                </Box>
              ))
            }
          </Paper>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Anulo</Button>
        <Button variant="contained" color="secondary" disabled={!canApply || applying}
          onClick={() => onApply({ workingTypeCode: selectedType, dateFrom, dateTo, workerIds: [...selectedWorkers] })}
          sx={{ borderRadius: 2, minWidth: 120 }}
          startIcon={applying ? <CircularProgress size={14} color="inherit" /> : null}>
          Apliko ({selectedWorkers.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Deterministic pastel colour from a string
function strColor(str) {
  const PALETTE = ['#14b8a6','#6366f1','#f59e0b','#10b981','#3b82f6','#ec4899','#8b5cf6','#ef4444','#0ea5e9','#84cc16'];
  let h = 0; for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// ── Shift Card ─────────────────────────────────────────────────────────────
function ShiftCard({ schedule, onEdit, onDelete, onAssign, canEdit }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState(null); // null = loading

  useEffect(() => {
    apiService().getById('workSchedules', `${schedule.id}/employees`)
      .then(r => setEmployees(r?.data || []))
      .catch(() => setEmployees([]));
  }, [schedule.id]);

  const workH = calcWorkHours(schedule.start_time, schedule.end_time, schedule.break_minutes);
  const workerCount = employees === null ? null : employees.length;
  const MAX_AVATARS = 6;
  const shown    = (employees || []).slice(0, MAX_AVATARS);
  const overflow = (employees || []).length - MAX_AVATARS;

  const stats = [
    { icon: <GroupsIcon sx={{ fontSize: 14 }} />, value: workerCount === null ? '…' : workerCount, label: 'Punëtorë', color: '#10b981' },
    { icon: <CoffeeIcon  sx={{ fontSize: 14 }} />, value: `${schedule.break_minutes}m`,             label: 'Pauzë',     color: '#6366f1' },
    { icon: <TimerIcon   sx={{ fontSize: 14 }} />, value: workH || '—',                             label: 'Punë neto', color: '#3b82f6' },
  ];

  return (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 2,
      height: '100%', display: 'flex', flexDirection: 'column',
      transition: 'box-shadow .15s, border-color .15s',
      '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderColor: '#d1d5db' },
    }}>
      <Box sx={{ p: 1.25, flex: 1 }}>

        {/* ── Title row (compact) ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.25,
              bgcolor: '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <ScheduleIcon sx={{ fontSize: 14, color: '#374151' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }} noWrap>
                {schedule.name}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.3}>
                <AccessTimeIcon sx={{ fontSize: 10, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: '0.66rem', color: '#6b7280', fontWeight: 600 }}>
                  {fmtTime(schedule.start_time)}–{fmtTime(schedule.end_time)}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          {canEdit && (
            <Stack direction="row">
              <Tooltip title={t('common.edit')}>
                <IconButton size="small" onClick={() => onEdit(schedule)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.delete')}>
                <IconButton size="small" onClick={() => onDelete(schedule.id)} sx={{ p: 0.25 }}>
                  <DeleteIcon sx={{ fontSize: 13, color: '#fca5a5' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        {/* ── Stats inline strip ── */}
        <Stack direction="row" spacing={0.75} mb={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {stats.map(s => (
            <Box key={s.label} sx={{
              px: 0.75, py: 0.3, borderRadius: 1, bgcolor: '#f9fafb', border: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', gap: 0.4,
            }}>
              <Box sx={{ color: s.color, lineHeight: 0, flexShrink: 0, '& svg': { fontSize: 11 } }}>{s.icon}</Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#111827' }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af' }}>{s.label}</Typography>
            </Box>
          ))}
        </Stack>

        {/* ── Work day badges ── */}
        <Stack direction="row" spacing={0.4} mb={1}>
          {DAYS_EN.map(day => {
            const active = (schedule.work_days || []).includes(day);
            return (
              <Box key={day} sx={{
                width: 18, height: 18, borderRadius: 0.75,
                fontSize: '0.55rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: active ? '#111827' : '#f3f4f6',
                color: active ? '#fff' : '#9ca3af',
                userSelect: 'none',
              }}>
                {DAYS_SQ[day].slice(0, 2)}
              </Box>
            );
          })}
        </Stack>

        {/* ── Worker avatars ── */}
        <Stack direction="row" alignItems="center" spacing={-0.5}>
          {employees === null ? (
            <CircularProgress size={12} thickness={4} />
          ) : shown.length === 0 ? (
            <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>S'ka punëtorë</Typography>
          ) : (
            <>
              {shown.map((emp, i) => {
                const name = `${emp.first_name || emp.FirstName || ''}`.trim() || '?';
                const bg = strColor(name);
                return (
                  <Tooltip key={emp.worker_id || i} title={`${emp.first_name || ''} ${emp.last_name || ''}`.trim()}>
                    <Avatar sx={{
                      width: 22, height: 22, fontSize: '0.58rem', fontWeight: 700,
                      bgcolor: bg, color: '#fff',
                      border: '2px solid #fff',
                      zIndex: shown.length - i,
                    }}>
                      {name[0].toUpperCase()}
                    </Avatar>
                  </Tooltip>
                );
              })}
              {overflow > 0 && (
                <Avatar sx={{
                  width: 22, height: 22, fontSize: '0.55rem', fontWeight: 700,
                  bgcolor: '#e5e7eb', color: '#6b7280',
                  border: '2px solid #fff', zIndex: 0,
                }}>
                  +{overflow}
                </Avatar>
              )}
            </>
          )}
        </Stack>
      </Box>

      <Box sx={{ borderTop: '1px solid #f3f4f6', p: 0.75 }}>
        <Button fullWidth size="small"
          startIcon={<GroupsIcon sx={{ fontSize: 12 }} />}
          onClick={() => onAssign(schedule)}
          sx={{
            borderRadius: 1, textTransform: 'none', fontWeight: 600,
            fontSize: '0.7rem', color: '#374151',
            bgcolor: '#f9fafb', border: '1px solid #e5e7eb', py: 0.25,
            '&:hover': { bgcolor: '#f0f4ff', borderColor: '#c7d2fe', color: '#4f46e5' },
          }}>
          {t('workSchedules.viewWorkers', 'Shiko punëtorët')}
        </Button>
      </Box>
    </Box>
  );
}

// ═══ Main Component ═══════════════════════════════════════════════════════════
export default function WorkSchedulesList() {
  const { t } = useTranslation();
  const { hasAnyRole } = usePermissions();
  const canEditSchedule = hasAnyRole(['super_admin', 'company_admin', 'hr_manager']);
  const [editMode, setEditMode] = useState(false);
  const [schedules,  setSchedules]  = useState([]);
  const [workers,    setWorkers]    = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [departments, setDepartments] = useState([]);
  const [workTypes,  setWorkTypes]  = useState([]);
  const [scheduleSummary, setScheduleSummary] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [applyingWT, setApplyingWT] = useState(false);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', variant: 'info' });

  const [scheduleDlg,  setScheduleDlg]  = useState({ open: false, data: null });
  const [assignDlg,    setAssignDlg]    = useState({ open: false, schedule: null, workerSearch: '' });
  const [workTypeDlg,  setWorkTypeDlg]  = useState({ open: false });
  const [confirmDel,   setConfirmDel]   = useState({ open: false, id: null });
  const [activeTab,    setActiveTab]    = useState('all');
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [rowMenu,      setRowMenu]      = useState({ anchor: null, schedule: null });

  const showToast = (message, variant = 'success') =>
    setToast({ open: true, message, variant });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, wRes, pRes, wtRes, sumRes, dRes] = await Promise.all([
        apiService().get('workSchedules'),
        apiService().get('workers'),
        apiService().get('projects'),
        fetchWorkingTypes().catch(() => []),
        apiService().get('workSchedules/summary'),
        apiService().get('department').catch(() => apiService().get('departments').catch(() => null)),
      ]);
      setSchedules(sRes?.data || []);
      setWorkers(activeWorkers(wRes?.data));
      setProjects(pRes?.data || []);
      setWorkTypes(Array.isArray(wtRes) ? wtRes.filter(x => x.isActive !== false) : []);
      setScheduleSummary(sumRes?.data || null);
      setDepartments(dRes?.data || []);
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save schedule ────────────────────────────────────────────────────────
  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (data.id) {
        const r = await apiService().put('workSchedules', data);
        setSchedules(p => p.map(s => s.id === data.id ? (r?.data || data) : s));
        showToast(t('workSchedules.scheduleUpdated'));
      } else {
        const r = await apiService().post('workSchedules', data);
        if (r?.data) setSchedules(p => [...p, r.data]);
        showToast(t('workSchedules.scheduleAdded'));
      }
      setScheduleDlg({ open: false, data: null });
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  // ── Delete schedule ──────────────────────────────────────────────────────
  // Kopjo shabllonin e turnit (bashkë me llojin e punës) — serveri e emërton «(kopje)».
  const handleCopySchedule = async (schedule) => {
    if (!schedule) return;
    try {
      await apiService().post(`workSchedules/${schedule.id}/copy`, {});
      showToast(`Orari «${schedule.name}» u kopjua`);
      load();
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await apiService().delete('workSchedules', id);
      setSchedules(p => p.filter(s => s.id !== id));
      showToast(t('workSchedules.scheduleDeleted'));
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    setConfirmDel({ open: false, id: null });
  };

  // ── Assign worker ────────────────────────────────────────────────────────
  const handleAssign = async (workerId, effectiveFrom) => {
    if (!assignDlg.schedule) return;
    setLoadingAssign(true);
    try {
      await apiService().post(`workSchedules/${assignDlg.schedule.id}/assign`, {
        worker_id: workerId,
        effective_from: effectiveFrom,
      });
      showToast(t('workSchedules.scheduleAssigned'));
      load();
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    finally { setLoadingAssign(false); }
  };

  // ── Apply Working Type to selected workers ───────────────────────────────
  const handleApplyWorkingType = async ({ workingTypeCode, dateFrom, dateTo, workerIds, force = false }) => {
    setApplyingWT(true);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/workSchedules/applyWorkingType`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ working_type_code: workingTypeCode, date_from: dateFrom, date_to: dateTo, worker_ids: workerIds, force }),
        }
      );
      const data = await res.json();
      if (data.success) {
        showToast(t('workSchedules.workTypeApplied', { count: data.affected }));
        setWorkTypeDlg({ open: false });
      } else if (res.status === 409 && data.code === 'PERIOD_CLOSED') {
        // Muaj me listë page të mbyllur: histori financiare. Vetëm kush ka
        // payroll.manage mund ta detyrojë, dhe vetëm pasi ta lexojë.
        const months = (data.closedMonths || []).join(', ');
        if (data.canForce && window.confirm(`Intervali prek muaj me listë page të mbyllur (${months}). Ndryshimi do të prekë histori financiare të mbyllur dhe do të auditohet.\nVazhdon?`)) {
          setApplyingWT(false);
          return handleApplyWorkingType({ workingTypeCode, dateFrom, dateTo, workerIds, force: true });
        }
        showToast(data.error || `Muaj i mbyllur: ${months}`, 'error');
      } else {
        showToast(data.error || t('common.error'), 'error');
      }
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    finally { setApplyingWT(false); }
  };

  // ── Remove worker from schedule ──────────────────────────────────────────
  const handleRemove = async (workerId) => {
    if (!assignDlg.schedule) return;
    setLoadingAssign(true);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/workSchedules/${assignDlg.schedule.id}/assign/${workerId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );
      showToast(t('workSchedules.assignmentRemoved'));
      load();
    } catch (e) { showToast(t('common.error') + ': ' + e.message, 'error'); }
    finally { setLoadingAssign(false); }
  };

  // ── Filtered schedules + assignment counts ─────────────────────────────────
  const filteredSchedules = useMemo(() => {
    let list = schedules || [];
    // Project filter
    if (activeTab !== 'all') {
      list = list.filter(s => String(s.project_id || s.ProjectId || '') === activeTab);
    }
    // Search filter
    if (assignSearch.trim()) {
      const q = assignSearch.toLowerCase().trim();
      list = list.filter(s => (s.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [schedules, activeTab, assignSearch]);

  // Departamentet sipas id-së për të treguar ku janë punëtorët
  const deptById = useMemo(() => {
    const m = new Map();
    (departments || []).forEach(d => m.set(String(d.Id ?? d.id), d.Name ?? d.name ?? ''));
    return m;
  }, [departments]);

  // Workers assigned per schedule (when API returns it on the schedule object)
  const workersForSchedule = (s) => {
    const ids = s.worker_ids || s.workerIds || s.assigned_workers || [];
    return (workers || []).filter(w => ids.map(String).includes(String(w.Id || w.id)));
  };

  return (
    <Box>
      <Stack spacing={{ xs: 2, md: 2.5 }}>

        {/* ── 1. Page header ───────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.025em', color: 'text.primary', lineHeight: 1.1 }}>
              {t('workSchedules.title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.9375rem' }}>
              {schedules.length} orare · {scheduleSummary?.totalWorkers ?? 0} punëtorë me orar · {scheduleSummary?.totalWeeklyHours ?? 0}h/javë
            </Typography>
          </Box>
        </Stack>

        {/* ── 2. Compact toolbar ──────────────────────────────────────────── */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
          sx={{ flexWrap: { md: 'wrap' }, rowGap: 1 }}
        >
          <TextField
            size="small"
            placeholder="Kërko orar…"
            value={assignSearch}
            onChange={e => setAssignSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
            }}
            sx={{ flex: 1, minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 34, bgcolor: 'background.paper', fontSize: '0.875rem' }, '& .MuiOutlinedInput-input': { py: 0 } }}
          />
          <TextField
            type="date" size="small"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 34, bgcolor: 'background.paper' }, '& .MuiOutlinedInput-input': { py: 0, fontSize: '0.8125rem' } }}
          />
          {projects.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={activeTab}
                onChange={e => setActiveTab(e.target.value)}
                displayEmpty
                sx={{ height: 34, borderRadius: 1.5, fontSize: '0.8125rem', bgcolor: 'background.paper' }}
              >
                <MenuItem value="all">Të gjithë projektet</MenuItem>
                {projects.map(p => (
                  <MenuItem key={p.Id || p.id} value={String(p.Id || p.id)}>{p.Name || p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ flex: { md: 1 } }} />
          {canEditSchedule && (
            <>
              <Button
                variant="outlined"
                onClick={() => setWorkTypeDlg({ open: true })}
                sx={{ height: 34, borderRadius: 1.5, px: 1.5, fontSize: '0.8125rem' }}
                startIcon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
              >
                Llojet
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '15px !important' }} />}
                onClick={() => setScheduleDlg({ open: true, data: null })}
                sx={{ height: 34, borderRadius: 1.5, px: 1.5, fontSize: '0.8125rem' }}
              >
                Orar i ri
              </Button>
            </>
          )}
        </Stack>

        {/* ── 3. Workforce stats strip (inline, no big cards) ─────────────── */}
        {scheduleSummary?.byWorkType?.length > 0 && (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1}>
              <Typography variant="overline" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                Lloji i punës
              </Typography>
              {scheduleSummary.byWorkType.map((wt, i) => {
                const TYPE_COLORS = { Productive: '#10B981', 'Sick Leave': '#EF4444', Remote: '#3D8FA3', 'Remote Work': '#3D8FA3' };
                const c = TYPE_COLORS[wt.name] || '#F59E0B';
                return (
                  <Stack key={wt.name} direction="row" alignItems="center" spacing={0.875}>
                    {i > 0 && <Box sx={{ width: 1, height: 16, bgcolor: 'divider', mr: 1 }}/>}
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }}/>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
                      {wt.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      {wt.weeklyHours}h · {wt.workers} punëtorë
                    </Typography>
                    <Chip
                      label={`${wt.percentage}%`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.6875rem', fontWeight: 600, bgcolor: alpha(c, 0.12), color: c, border: 0 }}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* ── 4. Operations list — one row per shift ───────────────────────── */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress size={28} /></Box>
        ) : filteredSchedules.length === 0 ? (
          <Paper elevation={0} sx={{
            p: 6, textAlign: 'center', borderRadius: 2,
            border: '1px dashed', borderColor: 'divider', bgcolor: 'background.paper',
          }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'grey.100', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
              <ScheduleIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Box>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {schedules.length === 0 ? t('workSchedules.noSchedulesDefined', 'Nuk ka orare të krijuara') : 'Asnjë orar me këto filtra'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {schedules.length === 0 ? t('workSchedules.addFirstSchedule', 'Krijo orarin e parë për të filluar') : 'Provo të ndryshosh filtrat ose pastro kërkimin'}
            </Typography>
            {canEditSchedule && schedules.length === 0 && (
              <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => setScheduleDlg({ open: true, data: null })}
                sx={{ height: 38, borderRadius: 1.5 }}>
                Krijo orarin e parë
              </Button>
            )}
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            {filteredSchedules.map((s, idx) => {
              const wf = workersForSchedule(s);
              const days = s.work_days || s.workDays || [];
              const netMin = calcNetMinutes(s.start_time, s.end_time, s.break_minutes);
              const net = fmtHM(netMin);
              const weekly = netMin && days.length ? fmtHM(netMin * days.length) : null;
              const locations = groupByLocation(wf, deptById);
              const isUnassigned = wf.length === 0;
              return (
                <Stack
                  key={s.id}
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={{ xs: 1.5, md: 2 }}
                  sx={{
                    px: { xs: 1.75, md: 2 }, py: 1.5,
                    borderBottom: idx < filteredSchedules.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    transition: 'background-color 0.12s ease',
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                >
                  {/* PRIMARY: time + name (left) */}
                  <Box sx={{ minWidth: { md: 180 }, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="baseline" spacing={0.875}>
                      <Typography sx={{
                        fontSize: '1.375rem', fontWeight: 700, color: 'text.primary',
                        letterSpacing: '-0.02em', lineHeight: 1.1,
                        fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums',
                      }}>
                        {fmtTime(s.start_time)}
                      </Typography>
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.9375rem', fontWeight: 500 }}>→</Typography>
                      <Typography sx={{
                        fontSize: '1.375rem', fontWeight: 700, color: 'text.primary',
                        letterSpacing: '-0.02em', lineHeight: 1.1,
                        fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums',
                      }}>
                        {fmtTime(s.end_time)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500, mt: 0.25 }} noWrap>
                      {s.name || 'Pa emër'}
                    </Typography>
                    {/* Lloji i punës i turnit — i paguar / i papaguar, i dukshëm te lista. */}
                    {s.working_type_code && (() => { const wt = workTypes.find(x => x.code === s.working_type_code); return (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.4 }} data-testid="shift-working-type">
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: wt?.color || '#64748B' }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#374151' }} noWrap>{wt?.name || s.working_type_code}</Typography>
                        <PaidChip paid={wt ? wt.isPaid : null} accrue={wt?.payrollImpact === 'ACCRUE'} />
                      </Stack>); })()}
                  </Box>

                  {/* PRIMARY: employees (center, takes flex) */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {isUnassigned ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }}/>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'warning.dark', fontWeight: 500 }}>
                          Pa caktim
                        </Typography>
                      </Stack>
                    ) : (
                      <>
                        <Stack direction="row" sx={{
                          '& > *:not(:first-of-type)': { ml: -1 },
                        }}>
                          {wf.slice(0, 5).map(w => {
                            const deptName = (w.Department != null && deptById.get(String(w.Department)))
                              || w.DepartmentName || w.Position || '';
                            return (
                              <Tooltip
                                key={w.Id || w.id}
                                arrow
                                title={`${w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`.trim()}${deptName ? ` · ${deptName}` : ''}`}
                              >
                                <Avatar
                                  sx={{
                                    width: 26, height: 26,
                                    fontSize: '0.6875rem', fontWeight: 600,
                                    bgcolor: 'primary.soft', color: 'primary.main',
                                    border: '2px solid white',
                                  }}
                                >
                                  {(w.FirstName || '?')[0]}{(w.LastName || '')[0]}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                          {wf.length > 5 && (
                            <Box sx={{
                              width: 26, height: 26, borderRadius: '50%',
                              border: '2px solid white',
                              bgcolor: 'grey.100', color: 'text.secondary',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.6875rem', fontWeight: 600,
                            }}>
                              +{wf.length - 5}
                            </Box>
                          )}
                        </Stack>
                        <Stack sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary', fontWeight: 600, lineHeight: 1.25 }}>
                            {wf.length}
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                              {wf.length === 1 ? 'punëtor' : 'punëtorë'}
                            </Box>
                          </Typography>
                          {locations.length > 0 && (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.375, flexWrap: 'wrap', rowGap: 0.5 }}>
                              {locations.slice(0, 3).map(loc => (
                                <Chip
                                  key={loc.name}
                                  size="small"
                                  label={`${loc.name} · ${loc.count}`}
                                  sx={{
                                    height: 18, fontSize: '0.6875rem', fontWeight: 500,
                                    bgcolor: 'grey.100', color: 'text.secondary',
                                    '& .MuiChip-label': { px: 0.875 },
                                  }}
                                />
                              ))}
                              {locations.length > 3 && (
                                <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', alignSelf: 'center' }}>
                                  +{locations.length - 3}
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </>
                    )}
                  </Box>

                  {/* SECONDARY: meta — net hours + break + weekday labels */}
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0, color: 'text.secondary' }}>
                    {net && (
                      <Tooltip arrow title={weekly ? `${net}/ditë × ${days.length} ditë = ${weekly}/javë` : `${net}/ditë`}>
                        <Stack alignItems="flex-end" sx={{ lineHeight: 1 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                            {net}<Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>/ditë</Box>
                          </Typography>
                          {weekly && (
                            <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                              {weekly}/javë
                            </Typography>
                          )}
                        </Stack>
                      </Tooltip>
                    )}
                    {s.break_minutes > 0 && (
                      <>
                        <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'grey.300' }}/>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {s.break_minutes}m pauzë
                        </Typography>
                      </>
                    )}
                    {days.length > 0 && (
                      <>
                        <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'grey.300' }}/>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                          {DAYS_EN.map(d => {
                            const active = days.includes(d);
                            return (
                              <Typography
                                key={d}
                                title={DAYS_SQ[d]}
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: active ? 600 : 400,
                                  color: active ? 'text.primary' : 'text.disabled',
                                  textDecoration: active ? 'none' : 'line-through',
                                  textDecorationColor: 'grey.300',
                                }}
                              >
                                {DAYS_SQ[d][2]}
                              </Typography>
                            );
                          })}
                        </Stack>
                      </>
                    )}
                  </Stack>

                  {/* TERTIARY: actions — Assign + ⋯ menu */}
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Button
                      variant={isUnassigned ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAssignDlg({ open: true, schedule: s, workerSearch: '' })}
                      sx={{ height: 32, borderRadius: 1.5, px: 1.5, fontSize: '0.75rem', minWidth: 0 }}
                    >
                      Cakto
                    </Button>
                    {canEditSchedule && (
                      <IconButton
                        onClick={(e) => setRowMenu({ anchor: e.currentTarget, schedule: s })}
                        sx={{ width: 32, height: 32, borderRadius: 1.5 }}
                      >
                        <Box sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color: 'text.secondary' }}>⋯</Box>
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Paper>
        )}
      </Stack>

      {/* Row action menu */}
      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={() => setRowMenu({ anchor: null, schedule: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setScheduleDlg({ open: true, data: rowMenu.schedule }); setRowMenu({ anchor: null, schedule: null }); }}>
          <EditIcon sx={{ fontSize: 16, mr: 1.25, color: 'text.secondary' }}/>
          Ndrysho
        </MenuItem>
        <MenuItem onClick={() => { handleCopySchedule(rowMenu.schedule); setRowMenu({ anchor: null, schedule: null }); }} data-testid="schedule-copy">
          <ContentCopyIcon sx={{ fontSize: 16, mr: 1.25, color: 'text.secondary' }}/>
          Kopjo (me llojin e punës)
        </MenuItem>
        <MenuItem
          onClick={() => { setConfirmDel({ open: true, id: rowMenu.schedule?.id }); setRowMenu({ anchor: null, schedule: null }); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ fontSize: 16, mr: 1.25 }}/>
          Fshi
        </MenuItem>
      </Menu>

      {/* ── Dialogs ── */}
      <ScheduleDialog
        open={scheduleDlg.open} initial={scheduleDlg.data} workers={workers}
        onClose={() => setScheduleDlg({ open: false, data: null })}
        onSave={handleSave} saving={saving}
      />
      <AssignDialog
        open={assignDlg.open} schedule={assignDlg.schedule}
        workers={workers} departments={departments} projects={projects}
        initialSearch={assignDlg.workerSearch || ''} editMode={editMode}
        onClose={() => setAssignDlg({ open: false, schedule: null, workerSearch: '' })}
        onAssign={handleAssign} onRemove={handleRemove} loadingAssign={loadingAssign}
      />
      <WorkingTypeDialog
        open={workTypeDlg.open} workers={workers} workTypes={workTypes}
        onClose={() => setWorkTypeDlg({ open: false })}
        onApply={handleApplyWorkingType} applying={applyingWT}
      />
      <Dialog open={confirmDel.open} onClose={() => setConfirmDel({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('workSchedules.deleteScheduleTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>{t('workSchedules.deleteScheduleWarning')}</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setConfirmDel({ open: false, id: null })} sx={{ borderRadius: 1.5 }}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDel.id)}
            sx={{ borderRadius: 1.5, boxShadow: 'none' }}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
      <Toast open={toast.open} message={toast.message} variant={toast.variant}
        onClose={() => setToast(p => ({ ...p, open: false }))} />
    </Box>
  );
}
